/*
 * RS quick add popup (snippets/rs-quick-add-assets.liquid), on top of Dawn's quick-add.js.
 * Dawn's popup shows the <product-info> of the product page, but not the section's own <style>
 * (sizes and colors of each block, scoped to #shopify-section-<id>) and keeps every block.
 * Here, before the popup is filled:
 * - the section's <style> is copied into the popup, scoped to the popup;
 * - the blocks that do not belong in a quick add are removed (breadcrumb, short description,
 *   multi buy, pickup, icon with text, text, custom Liquid, share, apps, tabs). Removed, not only
 *   hidden: the multi buy and app blocks have their own forms and inputs.
 * Faster opening (1.70): Dawn's popup downloaded the whole product page at the click. Here the
 * product is loaded ahead (mouse resting on a card or its button for 150 ms, finger or mouse
 * pressing the button, keyboard focus), kept for 3 minutes, and after the first popup only the
 * product section is asked for (?section_id=, learned from the first answer; the whole page again
 * if that fails). No loading ahead on data saver or 2G connections, one at a time.
 */
customElements.whenDefined('quick-add-modal').then(() => {
  const QuickAddModal = customElements.get('quick-add-modal');
  const proto = QuickAddModal.prototype;
  if (proto.rsQuickAdd) return;
  proto.rsQuickAdd = true;

  const REMOVE = [
    '.rs-mp__crumbs',
    '.rs-mp__tabs',
    '.rs-mp__type--short_description',
    '.rs-mp__type--multi_buy',
    '.rs-mp__type--pickup',
    '.rs-mp__type--icon_text',
    '.rs-mp__type--text',
    '.rs-mp__type--custom_liquid',
    '.rs-mp__type--share',
    '.rs-mp__info > .shopify-app-block',
    '.rs-mp__info > [id^="shopify-block-"]',
  ].join(',');

  const preprocess = proto.preprocessHTML;
  proto.preprocessHTML = function (productElement) {
    preprocess.call(this, productElement);
    if (!productElement || !productElement.classList || !productElement.classList.contains('rs-mp')) return;

    productElement.querySelectorAll(REMOVE).forEach((el) => el.remove());
    productElement.classList.add('rs-mp--quick-add');

    // Only when the popup is filled (not on a variant change: the style is already there).
    const doc = productElement.ownerDocument;
    const sectionId = productElement.dataset.originalSection;
    if (!doc || !sectionId || this.modalContent.querySelector('style[data-rs-quick-add]')) return;

    const scope = `#${this.modalContent.id}`;
    const css = Array.from(doc.querySelectorAll('style'))
      .map((style) => style.textContent)
      .filter((text) => text.includes(`#shopify-section-${sectionId}`))
      .join('\n')
      .replaceAll(`#shopify-section-${sectionId}`, scope)
      .replaceAll(sectionId, `quickadd-${sectionId}`);
    if (!css) return;

    const style = document.createElement('style');
    style.dataset.rsQuickAdd = '';
    style.textContent = css;
    productElement.prepend(style);
  };
});

// Faster opening: load ahead, cache, product section only
customElements.whenDefined('quick-add-modal').then(() => {
  const proto = customElements.get('quick-add-modal').prototype;
  if (proto.rsQuickAddFast) return;
  proto.rsQuickAddFast = true;
  const modalShow = Object.getPrototypeOf(proto).show; // ModalDialog.show

  const TTL = 3 * 60 * 1000;
  const cache = new Map(); // product URL -> { time, promise of the HTML text }
  const themeId = (window.Shopify && window.Shopify.theme && window.Shopify.theme.id) || '';
  const storeKey = `rs-quick-add-section-${themeId}`;
  let sectionId = null;
  try {
    sectionId = sessionStorage.getItem(storeKey);
  } catch (e) {}
  let ahead = 0;

  const slowConnection = () => {
    const connection = navigator.connection;
    return !!connection && (connection.saveData || /2g$/.test(connection.effectiveType || ''));
  };

  const getText = (url, options) =>
    fetch(url, options).then((response) => {
      if (!response.ok) throw new Error(response.status);
      return response.text();
    });

  function load(url, early) {
    const hit = cache.get(url);
    if (hit && Date.now() - hit.time < TTL) return hit.promise;

    const options = early ? { priority: 'low' } : {};
    const whole = () => getText(url, options);
    let promise;
    if (sectionId) {
      const sectionUrl = new URL(url, window.location.origin);
      sectionUrl.searchParams.set('section_id', sectionId);
      // Another product template (other section id) or no product in the answer: whole page
      promise = getText(sectionUrl.pathname + sectionUrl.search, options)
        .then((text) => (text.includes('<product-info') ? text : Promise.reject(new Error('no product-info'))))
        .catch(whole);
    } else {
      promise = whole();
    }

    const entry = { time: Date.now(), promise };
    cache.set(url, entry);
    promise.catch(() => {
      if (cache.get(url) === entry) cache.delete(url);
    });
    return promise;
  }

  function loadAhead(button) {
    const url = button && button.getAttribute('data-product-url');
    if (!url || ahead > 0 || slowConnection()) return;
    const hit = cache.get(url);
    if (hit && Date.now() - hit.time < TTL) return;
    ahead += 1;
    load(url, true)
      .catch(() => {})
      .finally(() => {
        ahead -= 1;
      });
  }

  // Dawn's QuickAddModal.show, with the product from the cache / section
  proto.show = function (opener) {
    const url = opener.getAttribute('data-product-url');
    const spinner = opener.querySelector('.loading__spinner');
    opener.setAttribute('aria-disabled', true);
    opener.classList.add('loading');
    if (spinner) spinner.classList.remove('hidden');

    load(url, false)
      .then((text) => {
        const responseHTML = new DOMParser().parseFromString(text, 'text/html');
        const productElement = responseHTML.querySelector('product-info');
        if (!productElement) throw new Error('No product in the answer');

        if (productElement.dataset.section && productElement.dataset.section !== sectionId) {
          sectionId = productElement.dataset.section;
          try {
            sessionStorage.setItem(storeKey, sectionId);
          } catch (e) {}
        }

        this.preprocessHTML(productElement);
        HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

        if (window.Shopify && Shopify.PaymentButton) Shopify.PaymentButton.init();
        if (window.ProductModel) window.ProductModel.loadShopifyXR();
        this.modalContent.querySelector('product-component')?.dispatchViewEvent?.();

        modalShow.call(this, opener);
      })
      .catch((error) => {
        // The product page instead of a popup that never opens
        console.error('RS quick add: could not load the product', error);
        cache.delete(url);
        window.location.href = url;
      })
      .finally(() => {
        opener.removeAttribute('aria-disabled');
        opener.classList.remove('loading');
        if (spinner) spinner.classList.add('hidden');
      });
  };

  // Loading ahead: mouse resting on a card (or its button), press, keyboard focus
  const buttonIn = (element) =>
    element && element.closest && element.closest('.card-wrapper')?.querySelector('modal-opener [data-product-url]');
  let timer = null;
  let card = null;

  document.addEventListener('pointerover', (event) => {
    if (event.pointerType !== 'mouse') return;
    const wrapper = event.target.closest && event.target.closest('.card-wrapper');
    if (wrapper === card) return;
    card = wrapper;
    clearTimeout(timer);
    const button = buttonIn(wrapper);
    if (button) timer = setTimeout(() => loadAhead(button), 150);
  });
  document.addEventListener('pointerdown', (event) => {
    const button = event.target.closest && event.target.closest('modal-opener [data-product-url]');
    if (button) loadAhead(button);
  });
  document.addEventListener('focusin', (event) => {
    const button = event.target.closest && event.target.closest('modal-opener [data-product-url]');
    if (button) {
      clearTimeout(timer);
      timer = setTimeout(() => loadAhead(button), 150);
    }
  });
});

// Escape in a popup opened from the quick add (size guide, image zoom, info) closes only that
// popup: Dawn's quick add closes itself on the Escape keyup, which reaches it after the inner
// <dialog> has already closed on keydown.
(() => {
  let innerEscape = false;
  document.addEventListener(
    'keydown',
    (event) => {
      if (event.key === 'Escape') innerEscape = !!event.target.closest?.('quick-add-modal dialog[open]');
    },
    true
  );
  document.addEventListener(
    'keyup',
    (event) => {
      if (event.key !== 'Escape' || !innerEscape) return;
      innerEscape = false;
      event.stopPropagation();
    },
    true
  );
})();
